/** Settings: the organisation's AI readiness report, as a document to keep or hand to an auditor. */

import { useState } from 'react';

import { download, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { Button, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';

export function ReadinessPanel() {
  const r = useLoad(() => routes.readiness().catch(() => undefined), []);
  const [error, setError] = useState<string | undefined>();
  const save = async (format: 'md' | 'html') => {
    setError(undefined);
    try {
      await download(routes.readinessReportUrl(format), `ai-readiness.${format}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const gaps = r.data?.gaps.length;
  return (
    <ListGroup
      id="readiness"
      title="Readiness report"
      description="What this organisation has in place for AI, from what is known here: machines, policies, tool approvals, data protection, agent identities, classification, open approvals and adoption, with the gaps in plain sentences."
    >
      <ListRow
        title="The report"
        description={gaps === undefined ? 'Looking for gaps…' : gaps === 0 ? 'No gaps found.' : `${gaps} ${gaps === 1 ? 'gap' : 'gaps'} found; the report says what each one is.`}
        control={
          <>
            {gaps === undefined ? null : gaps === 0 ? <StatusMark state="connected" label="Ready" /> : <StatusMark state={r.data!.gaps.some((g) => g.severity === 'high') ? 'error' : 'idle'} label={`${gaps} ${gaps === 1 ? 'gap' : 'gaps'}`} />}
            <Button size="sm" variant="secondary" onPress={() => void save('html')}>
              Download
            </Button>
            <Button size="sm" variant="ghost" onPress={() => void save('md')}>
              As Markdown
            </Button>
          </>
        }
      />
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
